import { AfterViewInit, Component, ElementRef, HostBinding, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { ChimeMeetingService, DeviceLists, JoinOptions, PreviewDeviceState, PreviewResult } from "src/app/services/chime-meeting.service";
import { MeetingThemeService } from "src/app/services/meeting-theme.service";
import { SessionService } from "src/app/services/session.service";
import { avatarColor, formatDuration, initialsOf, MeetingLeftEvent } from "../meeting-room/meeting-utils";

type Stage = "lobby" | "meeting" | "left";
type AudioMode = "computer" | "none";
type SideView = "audio" | "devices" | "effects";

@Component({
  selector: "app-meeting-join",
  templateUrl: "./meeting-join.component.html",
  styleUrls: ["./meeting-join.component.scss"],
  standalone: false,
})
export class MeetingJoinComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("cam") previewVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild("meter") meter?: ElementRef<HTMLElement>;

  code = "";
  codeFromLink = false;
  inviteToken = "";
  displayName = "";
  preview: any = null;
  joined: any = null;
  error = "";
  cameraError = "";
  /** The device note's icon: the camera's when the camera is what failed, otherwise the microphone's. */
  cameraNoteIcon = "videocam_off";
  loading = false;
  /** True while the meeting details behind the link are being fetched. */
  detailsLoading = false;
  /**
   * True while the camera preview is starting. Starts true because the preview starts as soon as
   * the view exists; flipping it inside ngAfterViewInit would change a bound value mid-check.
   */
  cameraStarting = true;
  cameraOn = true;
  muted = false;
  audioMode: AudioMode = "computer";
  blur = false;
  blurSupported = false;
  canChooseSpeaker = false;
  sideView: SideView = "audio";
  devices: DeviceLists = { mics: [], speakers: [], cameras: [] };
  audioInputId = "";
  audioOutputId = "";
  videoInputId = "";
  stage: Stage = "lobby";
  leftInfo: MeetingLeftEvent | null = null;
  joinOptions: JoinOptions = {};
  summaryStatus = "";
  summaryBusy = false;
  /** Dismiss tried to close the tab and the browser refused. */
  closeBlocked = false;

  private previewRun = 0;
  private meterFrame = 0;
  private destroyed = false;

  constructor(
    private route: ActivatedRoute,
    private api: ApiService,
    private chime: ChimeMeetingService,
    private zone: NgZone,
    private themes: MeetingThemeService,
    private session: SessionService
  ) {}

  @HostBinding("class.meeting-theme-dark") get darkTheme() {
    return this.themes.isDark;
  }

  @HostBinding("class.meeting-theme-light") get lightTheme() {
    return !this.themes.isDark;
  }

  ngOnInit() {
    this.themes.attach();
    this.code = this.route.snapshot.paramMap.get("code") || "";
    this.codeFromLink = !!this.code;
    this.inviteToken = this.route.snapshot.queryParamMap.get("invite") || "";
    this.canChooseSpeaker = this.chime.canChooseSpeaker();
    this.chime.blurSupported().then((ok) => (this.blurSupported = ok));
    this.loadPreview();
  }

  ngAfterViewInit() {
    void this.startPreview();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.previewRun++;
    this.stopMeter();
    this.themes.detach();
    void this.chime.stopPreview();
  }

  get initials(): string {
    return this.displayName.trim() ? initialsOf(this.displayName) : "You";
  }

  get avatar() {
    return avatarColor(this.displayName.trim() || "you");
  }

  get canJoin(): boolean {
    return this.displayName.trim().length >= 2 && !!this.code.trim();
  }

  /** Why the join button is disabled, shown under it; empty once the form is complete. */
  get joinHint(): string {
    const name = this.displayName.trim();
    if (!name) return "Enter your name to join";
    if (name.length < 2) return "Your name needs at least 2 characters";
    if (!this.code.trim()) return "Enter the meeting code to join";
    return "";
  }

  /** The camera could not be opened (blocked, missing, busy or failed), as opposed to switched off. */
  get cameraUnavailable(): boolean {
    return !!this.cameraError && this.cameraNoteIcon === "videocam_off";
  }

  get whenLabel(): string {
    if (!this.preview?.startUtc) return "";
    const start = new Date(this.preview.startUtc);
    const end = this.preview.endUtc ? new Date(this.preview.endUtc) : null;
    const date = start.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    if (!end) return date;
    const endTime = end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${date} – ${endTime}`;
  }

  get leftTitle(): string {
    if (this.leftInfo?.endedForAll) return "You ended the meeting";
    switch (this.leftInfo?.reason) {
      case "ended": return "The meeting has ended";
      case "other-device": return "You joined from another device";
      case "removed": return "You were removed from the meeting";
      case "error": return "You were disconnected";
      default: return "You left the meeting";
    }
  }

  get leftText(): string {
    if (this.leftInfo?.endedForAll) return "The meeting has ended for everyone.";
    switch (this.leftInfo?.reason) {
      case "ended": return "Thanks for joining.";
      case "other-device": return "This meeting is continuing on your other device.";
      case "error": return "Check your connection, then rejoin.";
      default: return "Rejoin if you left by mistake.";
    }
  }

  get leftIcon(): string {
    return this.leftInfo?.reason === "error" ? "wifi_off" : "call_end";
  }

  get leftDuration(): string {
    return this.leftInfo?.durationMs ? formatDuration(this.leftInfo.durationMs) : "";
  }

  get canRejoin(): boolean {
    return this.leftInfo?.reason !== "ended" && this.leftInfo?.reason !== "removed";
  }

  get isHost(): boolean {
    const host = String(this.preview?.hostEmail || "").trim().toLowerCase();
    const me = String(this.session.getMainUsername() || this.session.getUsername() || "").trim().toLowerCase();
    return !!host && !!me && host === me;
  }

  async loadPreview() {
    if (!this.code) return;
    this.detailsLoading = true;
    try {
      this.preview = await firstValueFrom(this.api.resolveGuestMeeting({
        code: this.code,
        inviteToken: this.inviteToken,
      }));
    } catch (err: any) {
      // Only a refused link is "not valid"; a dropped connection or a server fault is not the link's fault.
      const status = Number(err?.status) || 0;
      this.error =
        err?.error?.message ||
        (status >= 400 && status < 500
          ? "This meeting link is not valid."
          : "The meeting details could not be loaded. Check your connection and try again.");
    } finally {
      this.detailsLoading = false;
    }
  }

  // ---- Preview and devices ----

  async startPreview() {
    if (this.destroyed || this.stage !== "lobby") return;
    const video = this.previewVideo?.nativeElement;
    if (!video) return;
    const run = ++this.previewRun;
    this.cameraError = "";
    this.cameraStarting = this.cameraOn;
    this.stopMeter();
    try {
      const started = await this.chime.startPreview(video, {
        cameraOn: this.cameraOn,
        noAudio: this.audioMode === "none",
        audioInputId: this.audioInputId,
        videoInputId: this.videoInputId,
        blur: this.blur,
      });
      if (run !== this.previewRun) return;
      this.cameraStarting = false;
      if (started.analyser) this.startMeter(started.analyser);
      // The switches show what is really on: a camera that did not open is off, and so is a
      // microphone that is blocked, missing or busy (the room would join muted anyway).
      if (this.cameraOn && started.camera !== "on") this.cameraOn = false;
      if (started.mic !== "on" && started.mic !== "off") this.muted = true;
      this.cameraError = this.deviceNote(started);
      this.cameraNoteIcon = started.camera !== "off" && started.camera !== "on" ? "videocam_off" : "mic_off";
    } catch {
      if (run !== this.previewRun) return;
      this.cameraStarting = false;
      this.cameraOn = false;
      this.cameraNoteIcon = "videocam_off";
      this.cameraError = "The camera and microphone could not be started. Reload the page and try again, or join without them.";
    }
    await this.loadDevices();
  }

  /** One line under the preview for whatever did not open; empty when everything asked for did. */
  private deviceNote(started: PreviewResult): string {
    const { camera, mic } = started;
    if (camera === "blocked" && mic === "blocked") {
      return "Your browser is blocking the camera and microphone. Allow access in the address bar, or join without them.";
    }
    const cameraText: Record<PreviewDeviceState, string> = {
      off: "",
      on: "",
      blocked: "Your browser is blocking the camera. Allow it in the address bar to be seen.",
      missing: "No camera was found. Others will see your initials.",
      busy: "Your camera is in use by another app. Close it, then turn the camera on again.",
      failed: "The camera could not be started.",
    };
    const micText: Record<PreviewDeviceState, string> = {
      off: "",
      on: "",
      blocked: "Your browser is blocking the microphone. Allow it in the address bar, or join without audio.",
      missing: "No microphone was found. You can still listen and chat.",
      busy: "Your microphone is in use by another app.",
      failed: "The microphone could not be started.",
    };
    return [cameraText[camera], micText[mic]].filter(Boolean).join(" ");
  }

  async loadDevices() {
    try {
      this.devices = await this.chime.listDevices();
      this.audioInputId = this.keep(this.audioInputId, this.devices.mics);
      this.audioOutputId = this.keep(this.audioOutputId, this.devices.speakers);
      this.videoInputId = this.keep(this.videoInputId, this.devices.cameras);
    } catch {
      // Device names need permission; the pickers stay empty until it is granted.
    }
  }

  toggleCamera() {
    this.cameraOn = !this.cameraOn;
    void this.startPreview();
  }

  toggleMic() {
    if (this.audioMode === "none") return;
    this.muted = !this.muted;
  }

  setAudioMode(mode: AudioMode) {
    if (this.audioMode === mode) return;
    this.audioMode = mode;
    void this.startPreview();
  }

  /** The right-hand card swaps between audio options, device settings and background filters. */
  openSide(view: SideView) {
    this.sideView = this.sideView === view ? "audio" : view;
    if (this.sideView === "devices") void this.loadDevices();
  }

  chooseMic(deviceId: string) {
    this.audioInputId = deviceId;
    void this.startPreview();
  }

  chooseSpeaker(deviceId: string) {
    this.audioOutputId = deviceId;
  }

  chooseCamera(deviceId: string) {
    this.videoInputId = deviceId;
    if (this.cameraOn) void this.startPreview();
  }

  setBlur(on: boolean) {
    this.blur = on;
    if (this.cameraOn) void this.startPreview();
  }

  // ---- Joining and leaving ----

  async join() {
    if (this.loading || !this.canJoin) return;
    this.loading = true;
    this.error = "";
    await this.stopPreview();
    try {
      this.joined = await firstValueFrom(this.api.joinGuestMeeting({
        code: this.code.trim(),
        inviteToken: this.inviteToken,
        displayName: this.displayName.trim(),
      }));
      this.joinOptions = {
        noAudio: this.audioMode === "none",
        audioInputId: this.audioInputId,
        audioOutputId: this.audioOutputId,
        videoInputId: this.videoInputId,
        blur: this.blur,
      };
      this.leftInfo = null;
      this.stage = "meeting";
    } catch (err: any) {
      this.error = err?.error?.message || "Unable to join this meeting.";
      if (this.stage === "lobby") setTimeout(() => this.startPreview());
    } finally {
      this.loading = false;
    }
  }

  onLeft(event: MeetingLeftEvent) {
    this.joined = null;
    this.leftInfo = event;
    this.stage = "left";
    this.summaryStatus = "";
    this.closeBlocked = false;
    if (event.endedForAll) void this.sendHostSummary();
  }

  /** The host chose "End meeting for all": end it for everyone and email an AI summary to invitees. */
  private async sendHostSummary(): Promise<void> {
    const code = String(this.preview?.publicCode || this.code || "").trim();
    if (!code || !this.isHost || this.summaryBusy) return;
    this.summaryBusy = true;
    this.summaryStatus = "Preparing the AI meeting summary for participants…";
    try {
      const res: any = await firstValueFrom(this.api.endMeetingWithSummary(code));
      const row = res?.data || res || {};
      this.summaryStatus =
        String(row.message || "").trim() ||
        (Number(row.emailedCount) > 0
          ? `Summary emailed to ${row.emailedCount} participant${Number(row.emailedCount) === 1 ? "" : "s"}.`
          : "Meeting ended. The summary email could not be sent.");
    } catch (err: any) {
      this.summaryStatus = err?.error?.message || "Meeting ended. The summary email could not be sent.";
    } finally {
      this.summaryBusy = false;
    }
  }

  rejoin() {
    void this.join();
  }

  dismiss() {
    window.close();
    // Browsers only let a page close a tab that was opened by a link or script with no history
    // behind it; if this one is still open, say so instead of leaving the button looking dead.
    setTimeout(() => (this.closeBlocked = !window.closed), 300);
  }

  private async stopPreview() {
    this.previewRun++;
    this.stopMeter();
    await this.chime.stopPreview();
  }

  /** Drives the mic level bar from the analyser without running change detection every frame. */
  private startMeter(analyser: AnalyserNode) {
    const samples = new Uint8Array(analyser.fftSize);
    this.zone.runOutsideAngular(() => {
      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) {
          const value = (sample - 128) / 128;
          sum += value * value;
        }
        const level = this.muted ? 0 : Math.min(1, Math.sqrt(sum / samples.length) * 4);
        this.meter?.nativeElement.style.setProperty("--level", level.toFixed(2));
        this.meterFrame = requestAnimationFrame(tick);
      };
      this.meterFrame = requestAnimationFrame(tick);
    });
  }

  private stopMeter() {
    if (this.meterFrame) cancelAnimationFrame(this.meterFrame);
    this.meterFrame = 0;
    this.meter?.nativeElement.style.setProperty("--level", "0");
  }

  private keep(id: string, devices: MediaDeviceInfo[]): string {
    return id && devices.some((device) => device.deviceId === id) ? id : devices[0]?.deviceId || "";
  }
}
